import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, useForm } from "react-hook-form";

import { MessageResponseDto } from "@api/api";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { useQyCreateCampaign } from "@factory/query/campaign.query";
import { CampaignFormRule, CampaignFormSchema } from "@factory/form/form.rule";
import { campaignFormDefault } from "@factory/form/form.default";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { Constraint } from "@core/model/constraint.enum";
import { useUserIdentityContext } from "@core/services/user-identity.context";

export function useNewCampaign() {
  // LOCAL DECLARATION
  const { getUserRole } = useUserIdentityContext();
  const navigate = useNavigate();
  const { mapCreateCampaign } = useFormApiMapper();
  const { showError, showSuccess, showWarning } = useNotificationContext();

  // QUERIES
  const handleApiSuccess = (response: MessageResponseDto) => {
    const data = response.data as any;

    showSuccess(`New Campaign is created`);
    navigate(`../${data.code}`);
  };
  const { mutate: addCampaign, isLoading: isCreating } =
    useQyCreateCampaign(handleApiSuccess);

  // LOCAL FUNCTIONS
  const formMethod = useForm<CampaignFormSchema>({
    defaultValues: campaignFormDefault,
    resolver: zodResolver(CampaignFormRule),
  });
  const { handleSubmit } = formMethod;

  const handleValidate = (form: CampaignFormSchema) => {
    const { tenant } = getUserRole();
    const updatedForm = {
      ...form,
      tenant,
    } as CampaignFormSchema;

    const formData = mapCreateCampaign(updatedForm);
    addCampaign(formData);
  };
  const handleValidateError = (err: FieldErrors<CampaignFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };

  return {
    isCreating,
    formMethod,
    navigate,
    handleSubmit,
    handleValidate,
    handleValidateError,
  };
}
